import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../UI/card';
import { Button } from '../UI/button';
import { Checkbox } from '../UI/checkbox';
import { EmptyState } from '../UI/empty-state';
import LoadingSpinner from '../UI/LoadingSpinner';
import { useLocale } from '../../contexts/LocaleContext';
import { themeClasses } from '../../config/themeClasses';
import { tenantCompanyService } from '../../services/tenantCompanyService';
import { getTenantSupplierId, getTenantSupplierName } from '../../utils/tenantSupplierUtils';
import TenantRiskBreakdownSections from './TenantRiskBreakdownSections';

const DEFAULT_WEIGHTS = {
  newsWeight: 0.5,
  pathWeight: 0.2,
  geoPoliticalWeight: 0.2,
  logisticsWeight: 0.1,
};

function WeightSlider({ id, label, value, onChange, disabled }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={id} className="text-sm font-medium text-text-primary">
          {label}
        </label>
        <span className="text-xs tabular-nums text-text-secondary">{value.toFixed(2)}</span>
      </div>
      <input
        id={id}
        type="range"
        min={0}
        max={1}
        step={0.05}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-2 w-full cursor-pointer accent-primary-500"
      />
    </div>
  );
}

function TenantAnalysisPanel({
  hasTenantProfile,
  tenantSuppliers = [],
  onGoToOverview,
  onAnalysisComplete,
}) {
  const { t } = useLocale();
  const [analysis, setAnalysis] = useState(null);
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [running, setRunning] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [includeWatchlist, setIncludeWatchlist] = useState(true);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState([]);
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);

  const supplierOptions = useMemo(
    () =>
      tenantSuppliers
        .map((item) => ({
          ticker: getTenantSupplierId(item),
          name: getTenantSupplierName(item),
        }))
        .filter((item) => item.ticker),
    [tenantSuppliers]
  );

  const loadLatest = useCallback(async () => {
    setLoadingLatest(true);
    try {
      const response = await tenantCompanyService.getLatestTenantRiskAnalysis();
      if (response.success) {
        setAnalysis(response.data || null);
      }
    } catch (_) {
      setAnalysis(null);
    } finally {
      setLoadingLatest(false);
    }
  }, []);

  useEffect(() => {
    if (!hasTenantProfile) {
      setAnalysis(null);
      return;
    }
    loadLatest();
  }, [hasTenantProfile, loadLatest]);

  const toggleSupplier = (ticker) => {
    setSelectedSupplierIds((prev) =>
      prev.includes(ticker)
        ? prev.filter((id) => id !== ticker)
        : [...prev, ticker]
    );
  };

  const handleRunAnalysis = async () => {
    setRunning(true);
    const response = await tenantCompanyService.analyzeTenantRisk(undefined, {
      selectedSupplierIds,
      includeWatchlistSuppliers: includeWatchlist,
      ...weights,
    });
    setRunning(false);

    if (response.success && response.data) {
      setAnalysis(response.data);
      onAnalysisComplete?.(response.data);
      toast.success(t('tenantGraphAnalysisRunSuccess'));
    } else {
      toast.error(response.message || t('tenantGraphAnalysisRunFailed'));
    }
  };

  if (!hasTenantProfile) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title={t('tenantAnalysisNoProfileTitle')}
        description={t('tenantAnalysisNoProfileDescription')}
        actionLabel={t('tenantAnalysisGoToOverview')}
        onAction={onGoToOverview}
      />
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Card className={themeClasses.appKit.sectionShell}>
        <CardHeader>
          <CardTitle>{t('tenantGraphAnalysisTitle')}</CardTitle>
          <CardDescription>{t('tenantGraphAnalysisDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {supplierOptions.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div>
                <p className="text-sm font-medium text-text-primary">
                  {t('tenantGraphAnalysisSelectSuppliers')}
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  {t('tenantGraphAnalysisSelectSuppliersHint')}
                </p>
              </div>
              <ul className="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-lg border border-border-light p-3">
                {supplierOptions.map((supplier) => (
                  <li key={supplier.ticker} className="flex items-center gap-2">
                    <Checkbox
                      id={`supplier-select-${supplier.ticker}`}
                      checked={selectedSupplierIds.includes(supplier.ticker)}
                      onCheckedChange={() => toggleSupplier(supplier.ticker)}
                      disabled={running}
                    />
                    <label
                      htmlFor={`supplier-select-${supplier.ticker}`}
                      className="cursor-pointer text-sm font-normal text-text-primary"
                    >
                      {supplier.name}
                      <span className="ml-1 text-text-tertiary">({supplier.ticker})</span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex items-center gap-2">
            <Checkbox
              id="include-watchlist-suppliers"
              checked={includeWatchlist}
              onCheckedChange={(checked) => setIncludeWatchlist(Boolean(checked))}
              disabled={running}
            />
            <label
              htmlFor="include-watchlist-suppliers"
              className="cursor-pointer text-sm font-normal text-text-primary"
            >
              {t('tenantGraphAnalysisIncludeWatchlist')}
            </label>
          </div>

          <div>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-border-light px-3 py-2 text-sm font-medium text-text-primary"
              onClick={() => setAdvancedOpen((open) => !open)}
            >
              {t('tenantGraphAnalysisAdvanced')}
              {advancedOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {advancedOpen ? (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <WeightSlider
                  id="weight-news"
                  label={t('tenantGraphAnalysisWeightNews')}
                  value={weights.newsWeight}
                  disabled={running}
                  onChange={(value) => setWeights((prev) => ({ ...prev, newsWeight: value }))}
                />
                <WeightSlider
                  id="weight-path"
                  label={t('tenantGraphAnalysisWeightPath')}
                  value={weights.pathWeight}
                  disabled={running}
                  onChange={(value) => setWeights((prev) => ({ ...prev, pathWeight: value }))}
                />
                <WeightSlider
                  id="weight-geo"
                  label={t('tenantGraphAnalysisWeightGeo')}
                  value={weights.geoPoliticalWeight}
                  disabled={running}
                  onChange={(value) => setWeights((prev) => ({ ...prev, geoPoliticalWeight: value }))}
                />
                <WeightSlider
                  id="weight-logistics"
                  label={t('tenantGraphAnalysisWeightLogistics')}
                  value={weights.logisticsWeight}
                  disabled={running}
                  onChange={(value) => setWeights((prev) => ({ ...prev, logisticsWeight: value }))}
                />
              </div>
            ) : null}
          </div>

          <Button type="button" onClick={handleRunAnalysis} disabled={running}>
            {running ? t('tenantGraphAnalysisRunning') : t('tenantGraphAnalysisRun')}
          </Button>

          {loadingLatest ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : analysis ? (
            <TenantRiskBreakdownSections analysis={analysis} />
          ) : (
            <p className="text-sm text-text-secondary">{t('tenantGraphAnalysisLatestEmpty')}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TenantAnalysisPanel;
